import { Input } from '@emailforge/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icon, type IconName } from '@/components/ui/icons';
import { useShortcut } from '@/lib/keyboard';

/**
 * Cmd+K palette: a single dialog that lets you fuzzy-search and trigger every
 * common nav target + a few in-app actions. Mounted once at the app shell so
 * the shortcut works from any screen.
 *
 * Keep the action list flat and short — adding 50 items defeats the point.
 * If a screen has a screen-specific action (e.g. "publish version" in editor),
 * keep that as a button inside the editor TopBar; the palette is for things
 * worth surfacing globally.
 */

type Action = {
  id: string;
  label: string;
  hint?: string;
  icon?: IconName;
  run: (nav: ReturnType<typeof useNavigate>) => void;
};

const ACTIONS: Action[] = [
  { id: 'nav.dashboard', label: 'Go to delivery health', hint: 'G D', icon: 'grid', run: (n) => n('/app') },
  { id: 'nav.templates', label: 'Go to templates', hint: 'G T', icon: 'doc', run: (n) => n('/app/templates') },
  { id: 'nav.gallery', label: 'Browse gallery', icon: 'folder', run: (n) => n('/app/gallery') },
  { id: 'nav.sends', label: 'Go to sends', hint: 'G S', icon: 'send', run: (n) => n('/app/sends') },
  { id: 'nav.experiments', label: 'Go to A/B experiments', hint: 'G E', icon: 'beaker', run: (n) => n('/app/ab') },
  { id: 'nav.automations', label: 'Go to automations', hint: 'G A', icon: 'bolt', run: (n) => n('/app/automations') },
  { id: 'nav.integrations', label: 'Go to integrations', icon: 'layers', run: (n) => n('/app/integrations') },
  { id: 'nav.senders', label: 'Verified senders', icon: 'mail', run: (n) => n('/app/senders') },
  { id: 'nav.tracking', label: 'Tracking domains', icon: 'link', run: (n) => n('/app/tracking-domains') },
  { id: 'nav.api-keys', label: 'API keys', icon: 'plug', run: (n) => n('/app/api-keys') },
  { id: 'nav.webhooks', label: 'Webhooks', icon: 'plug', run: (n) => n('/app/webhooks') },
  { id: 'nav.settings', label: 'Workspace settings', icon: 'gear', run: (n) => n('/app/settings') },
  { id: 'nav.tokens', label: 'Design tokens', icon: 'palette', run: (n) => n('/app/tokens') },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useShortcut('mod+k', () => setOpen((o) => !o), { allowInInput: true });

  // Reset query and selection on open. Focus the input on next paint so the
  // dialog animates in cleanly before we steal focus.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIdx(0);
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return ACTIONS;
    const q = query.toLowerCase();
    // Cheap fuzzy: substring on label OR ALL chars present in order. The
    // ordered-chars rule lets `gtmpl` match "Go to templates" without
    // requiring users to type the exact phrase.
    return ACTIONS.filter((a) => {
      const lab = a.label.toLowerCase();
      if (lab.includes(q)) return true;
      let i = 0;
      for (const ch of lab) {
        if (ch === q[i]) i += 1;
        if (i === q.length) return true;
      }
      return false;
    });
  }, [query]);

  // Keep activeIdx in range when the filtered list shrinks.
  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(Math.max(0, filtered.length - 1));
  }, [filtered.length, activeIdx]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const action = filtered[activeIdx];
      if (action) {
        action.run(navigate);
        setOpen(false);
      }
    }
  };

  return (
    <>
      <div
        onClick={() => setOpen(false)}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',
          zIndex: 80,
        }}
      />
      <div
        role="dialog"
        aria-label="Command palette"
        style={{
          position: 'fixed',
          top: '15vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(560px, calc(100vw - 32px))',
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-3)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          zIndex: 81,
          overflow: 'hidden',
        }}
        onKeyDown={onKeyDown}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            borderBottom: '1px solid var(--line-soft)',
          }}
        >
          <Icon.search size={14} />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command…"
            style={{ flex: 1, border: 'none', padding: 0, fontSize: 14, background: 'transparent' }}
          />
          <span
            className="mono"
            style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: 0.06 }}
          >
            ESC
          </span>
        </div>

        <div style={{ maxHeight: '50vh', overflowY: 'auto', padding: 6 }}>
          {filtered.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--ink-3)' }}>
              No matches.
            </div>
          )}
          {filtered.map((a, i) => {
            const Active = i === activeIdx;
            const IconCmp = a.icon ? Icon[a.icon] : null;
            return (
              <button
                key={a.id}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => {
                  a.run(navigate);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '10px 12px',
                  border: 'none',
                  background: Active ? 'var(--paper-2)' : 'transparent',
                  borderRadius: 'var(--r-2)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: 13.5,
                  color: 'var(--ink)',
                }}
              >
                {IconCmp && (
                  <span style={{ color: 'var(--ink-3)', display: 'inline-flex' }}>
                    <IconCmp size={13} />
                  </span>
                )}
                <span style={{ flex: 1 }}>{a.label}</span>
                {a.hint && (
                  <span
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: 'var(--ink-3)',
                      letterSpacing: 0.06,
                      padding: '2px 6px',
                      background: 'var(--paper-3)',
                      borderRadius: 4,
                    }}
                  >
                    {a.hint}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
